import React, { useEffect, useState, useRef } from "react";
import "./styles.css";

export default function App() {
  const [map, setMap] = useState(null);

  // ✅ [수정 핵심 1] 좌표와 텍스트(Data)는 useState로 관리
  const [originCoords, setOriginCoords] = useState(null);
  const [destCoords, setDestCoords] = useState(null);
  const [waypoints, setWaypoints] = useState([]); // 좌표 배열

  const [originInput, setOriginInput] = useState("");
  const [destInput, setDestInput] = useState("");
  const [waypointInputs, setWaypointInputs] = useState([]); // 입력 텍스트 배열

  // ✅ [수정 핵심 2] 지도 위의 마커(View)는 useRef로 관리 (렌더링과 무관하게 즉시 접근)
  const originMarker = useRef(null);
  const destMarker = useRef(null);
  const waypointMarkers = useRef([]);
  const routeLine = useRef(null); // 경로 선도 ref로 관리

  const [info, setInfo] = useState(null);

  const [searchResults, setSearchResults] = useState([]);
  const [activeType, setActiveType] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);

  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");

  const cardRef = useRef(null);
  const [departureTime, setDepartureTime] = useState("now");

  const [isLoading, setIsLoading] = useState(false);

  // 🗑️ 기존 JS 방식의 Preload useEffect는 제거하고, 아래 JSX 방식(div hidden)으로 대체했습니다.

  useEffect(() => {
    if (window.kakao && window.kakao.maps) {
      const container = document.getElementById("map");
      const options = {
        center: new window.kakao.maps.LatLng(37.4602, 126.4407),
        level: 7,
      };
      setMap(new window.kakao.maps.Map(container, options));
    }
  }, []);

  const categoryIcon = (code) => {
    switch (code) {
      case "MT1":
        return "🛒";
      case "CS2":
        return "🏪";
      case "PS3":
        return "🧸";
      case "SC4":
        return "🏫";
      case "AC5":
        return "🎓";
      case "PK6":
        return "🅿️";
      case "OL7":
        return "⛽";
      case "SW8":
        return "🚇";
      case "BK9":
        return "🏦";
      case "CT1":
        return "🎨";
      case "AG2":
        return "🏠";
      case "PO3":
        return "🏛️";
      case "AT4":
        return "🚩";
      case "AD5":
        return "🛏️";
      case "FD6":
        return "🍴";
      case "CE7":
        return "☕";
      case "HP8":
        return "🏥";
      case "PM9":
        return "💊";
      case "ADDR":
        return "📍";
      default:
        return "📍";
    }
  };

  const handleInputChange = (e, type, index = null) => {
    const value = e.target.value;
    setKeyword(value);
    setActiveType(type);
    setActiveIndex(index);

    if (type === "origin") setOriginInput(value);
    if (type === "destination") setDestInput(value);
    if (type === "waypoint") {
      const next = [...waypointInputs];
      next[index] = value;
      setWaypointInputs(next);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedKeyword(keyword), 500);
    return () => clearTimeout(t);
  }, [keyword]);

  useEffect(() => {
    if (!map || debouncedKeyword.length < 2) {
      setSearchResults([]);
      return;
    }

    const ps = new window.kakao.maps.services.Places();
    const geocoder = new window.kakao.maps.services.Geocoder();

    ps.keywordSearch(debouncedKeyword, (placesData, placesStatus) => {
      let combinedResults = [];
      if (placesStatus === window.kakao.maps.services.Status.OK) {
        combinedResults = [...placesData];
      }

      geocoder.addressSearch(debouncedKeyword, (addrData, addrStatus) => {
        if (addrStatus === window.kakao.maps.services.Status.OK) {
          const formattedAddrData = addrData.map((item) => ({
            place_name: item.address_name,
            address_name: item.road_address
              ? item.road_address.address_name
              : item.address.address_name,
            x: item.x,
            y: item.y,
            category_group_code: "ADDR",
            category_name: "행정구역 > 주소",
          }));
          combinedResults = [...formattedAddrData, ...combinedResults];
        }
        setSearchResults(combinedResults);
      });
    });
  }, [debouncedKeyword, map]);

  // ✅ [수정됨] 마커 생성 및 관리 로직 (useRef 사용)
  const selectPlace = (place) => {
    const coords = new window.kakao.maps.LatLng(place.y, place.x);
    map.setCenter(coords);

    let img;
    // activeType이 확실하지 않을 경우를 대비한 방어 코드
    if (activeType === "origin") img = "/marker_start@2x.png";
    else if (activeType === "destination") img = "/marker_end@2x.png";
    else if (activeType === "waypoint") img = "/marker_waypoint@2x.png";
    else img = "/marker_start@2x.png"; // 기본값

    const marker = new window.kakao.maps.Marker({
      position: coords,
      image: new window.kakao.maps.MarkerImage(
        img,
        new window.kakao.maps.Size(40, 50),
        { offset: new window.kakao.maps.Point(20, 50) }
      ),
    });

    if (activeType === "origin") {
      // 기존 마커 제거 (ref 사용으로 확실하게 제거됨)
      originMarker.current && originMarker.current.setMap(null);
      marker.setMap(map);
      originMarker.current = marker; // ref 업데이트

      setOriginCoords(coords);
      setOriginInput(place.place_name);
    }

    if (activeType === "destination") {
      destMarker.current && destMarker.current.setMap(null);
      marker.setMap(map);
      destMarker.current = marker;

      setDestCoords(coords);
      setDestInput(place.place_name);
    }

    if (activeType === "waypoint") {
      // 해당 인덱스의 기존 마커 제거
      if (waypointMarkers.current[activeIndex]) {
        waypointMarkers.current[activeIndex].setMap(null);
      }
      marker.setMap(map);
      waypointMarkers.current[activeIndex] = marker;

      const w = [...waypoints];
      w[activeIndex] = coords;
      setWaypoints(w);

      const i = [...waypointInputs];
      i[activeIndex] = place.place_name;
      setWaypointInputs(i);
    }

    setSearchResults([]);
    setKeyword("");
  };

  const addWaypoint = () => {
    setWaypointInputs([...waypointInputs, ""]);
    setWaypoints([...waypoints, null]);
    // 마커 배열에도 빈 공간(null) 추가
    waypointMarkers.current.push(null);
  };

  const removeWaypoint = (index) => {
    const newInputs = [...waypointInputs];
    const newWaypoints = [...waypoints];

    // 1. 지도에서 마커 지우기
    if (waypointMarkers.current[index]) {
      waypointMarkers.current[index].setMap(null);
    }
    // 2. 마커 배열(Ref)에서 제거
    waypointMarkers.current.splice(index, 1);

    newInputs.splice(index, 1);
    newWaypoints.splice(index, 1);

    setWaypointInputs(newInputs);
    setWaypoints(newWaypoints);
  };

  const resetAll = () => {
    // Ref를 사용하여 마커 즉시 제거
    originMarker.current && originMarker.current.setMap(null);
    destMarker.current && destMarker.current.setMap(null);
    waypointMarkers.current.forEach((m) => m && m.setMap(null));
    routeLine.current && routeLine.current.setMap(null);

    // Ref 초기화
    originMarker.current = null;
    destMarker.current = null;
    waypointMarkers.current = [];
    routeLine.current = null;

    // State 초기화
    setOriginCoords(null);
    setDestCoords(null);
    setWaypoints([]);
    setOriginInput("");
    setDestInput("");
    setWaypointInputs([]);
    setInfo(null);
  };

  const snapToRoad = async (coords) => {
    const lng = coords.getLng();
    const lat = coords.getLat();
    const offsets = [
      [0, 0],
      [0.002, 0],
      [-0.002, 0],
      [0, 0.002],
      [0, -0.002],
    ];
    for (const [dx, dy] of offsets) {
      try {
        const url = `https://kakao-snap.liluxx271.workers.dev/?origin=${
          lng + dx
        },${lat + dy}&destination=${lng + dx + 0.01},${
          lat + dy + 0.01
        }&_ts=${Date.now()}`;
        const res = await fetch(url);
        const data = await res.json();
        const road = data?.routes?.[0]?.sections?.[0]?.roads?.[0];
        if (road?.vertexes?.length >= 2) {
          return new window.kakao.maps.LatLng(
            road.vertexes[1],
            road.vertexes[0]
          );
        }
      } catch (e) {}
    }
    return null;
  };

  const calculateFareBySegments = (
    distanceKm,
    durationMin,
    departureHour,
    departureMinute = 0
  ) => {
    const TOTAL_DISTANCE = distanceKm * 1000;
    const TOTAL_MINUTES = durationMin;
    const getFareRule = (hour) => {
      if (hour === 22) return { base: 5800, distUnit: 120, timeUnit: 120 };
      if (hour >= 23 || hour < 2)
        return { base: 6700, distUnit: 140, timeUnit: 140 };
      if (hour >= 2 && hour < 4)
        return { base: 5800, distUnit: 120, timeUnit: 120 };
      return { base: 4800, distUnit: 100, timeUnit: 100 };
    };
    let currentMinuteOfDay = departureHour * 60 + departureMinute;
    let { base } = getFareRule(departureHour);
    let totalFare = base;
    let chargeableDistance = Math.max(0, TOTAL_DISTANCE - 1600);
    if (chargeableDistance === 0) return totalFare;
    let remainingMinutes =
      (chargeableDistance / TOTAL_DISTANCE) * TOTAL_MINUTES;
    while (remainingMinutes > 0 && chargeableDistance > 0) {
      const currentHour = Math.floor(currentMinuteOfDay / 60) % 24;
      const minutesToNextHour = 60 - (currentMinuteOfDay % 60);
      const segmentMinutes = Math.min(remainingMinutes, minutesToNextHour);
      const { distUnit, timeUnit } = getFareRule(currentHour);
      const segmentDistance =
        (chargeableDistance / remainingMinutes) * segmentMinutes;
      const segmentSeconds = segmentMinutes * 60;
      const speedKmh = (segmentDistance / segmentSeconds) * 3.6;
      if (speedKmh > 15) {
        totalFare += Math.floor(segmentDistance / 131) * distUnit;
      } else {
        totalFare += Math.floor(segmentSeconds / 30) * timeUnit;
      }
      chargeableDistance -= segmentDistance;
      remainingMinutes -= segmentMinutes;
      currentMinuteOfDay += segmentMinutes;
    }
    return Math.round(totalFare);
  };

  const findRoute = async () => {
    if (!originCoords || !destCoords) {
      alert("출발지와 도착지를 모두 입력하세요.");
      return;
    }
    setIsLoading(true);
    try {
      const [originSnap, destSnap] = await Promise.all([
        snapToRoad(originCoords),
        snapToRoad(destCoords),
      ]);
      if (!originSnap || !destSnap) {
        alert("도로를 찾을 수 없습니다. 위치를 조정해 주세요.");
        setIsLoading(false);
        return;
      }
      const wp = waypoints
        .filter(Boolean)
        .map((w, i) => `${w.getLng()},${w.getLat()},name=경유지${i + 1}`)
        .join("|");
      let departureTimeStr = null;
      if (departureTime && departureTime !== "now") {
        const now = new Date();
        const [hh, mm] = departureTime.split(":").map(Number);
        const dep = new Date(now);
        dep.setHours(hh, mm, 0, 0);
        if (dep < now) dep.setDate(dep.getDate() + 1);
        departureTimeStr =
          dep.getFullYear().toString() +
          String(dep.getMonth() + 1).padStart(2, "0") +
          String(dep.getDate()).padStart(2, "0") +
          String(dep.getHours()).padStart(2, "0") +
          String(dep.getMinutes()).padStart(2, "0");
      }
      const url =
        "https://kakao-snap.liluxx271.workers.dev/" +
        `?origin=${originSnap.getLng()},${originSnap.getLat()}` +
        `&destination=${destSnap.getLng()},${destSnap.getLat()}` +
        (wp ? `&waypoints=${encodeURIComponent(wp)}` : "") +
        (departureTimeStr ? `&departure_time=${departureTimeStr}` : "") +
        `&_ts=${Date.now()}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!data.routes || data.routes.length === 0) {
        alert("차량 접근이 어려운 지역이에요.");
        setIsLoading(false);
        return;
      }
      const route = data.routes[0];
      const distance = route.summary.distance / 1000;
      const duration = route.summary.duration / 60;
      let fareDeparture = new Date();
      if (departureTime && departureTime !== "now") {
        const [hh, mm] = departureTime.split(":").map(Number);
        fareDeparture.setHours(hh, mm, 0, 0);
        if (fareDeparture < new Date())
          fareDeparture.setDate(fareDeparture.getDate() + 1);
      }
      const fare = calculateFareBySegments(
        distance,
        duration,
        fareDeparture.getHours(),
        fareDeparture.getMinutes()
      );
      setInfo({
        distance: distance.toFixed(1),
        duration: duration.toFixed(0),
        fare: fare.toLocaleString(),
      });
      const path = [];
      route.sections.forEach((s) =>
        s.roads.forEach((r) => {
          for (let i = 0; i < r.vertexes.length; i += 2) {
            path.push(
              new window.kakao.maps.LatLng(r.vertexes[i + 1], r.vertexes[i])
            );
          }
        })
      );

      // 경로 라인도 Ref로 처리
      routeLine.current && routeLine.current.setMap(null);
      const line = new window.kakao.maps.Polyline({
        path,
        strokeWeight: 5,
        strokeColor: "#FF0000",
        strokeOpacity: 0.7,
      });
      line.setMap(map);
      routeLine.current = line;

      const bounds = new window.kakao.maps.LatLngBounds();
      path.forEach((p) => bounds.extend(p));
      map.setBounds(bounds);
      setTimeout(
        () =>
          cardRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          }),
        100
      );
    } catch (e) {
      alert("오류가 발생했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleReservation = () => {
    if (!info) return;
    const validWaypoints = waypointInputs.filter((w) => w.trim() !== "");
    const waypointText =
      validWaypoints.length > 0
        ? `\n📍 경유지: ${validWaypoints.join(", ")}`
        : "";

    // ✅ 더 상세해진 예약 가이드 양식
    const summaryText = `[공항친구 요금계산기- 예약 문의]
  --------------------------
  🛫 출발지: ${originInput}${waypointText}
  🛬 도착지: ${destInput}
  🕒 소요시간: 약 ${info.duration}분
  💰 예상요금: ${info.fare}원
  (실제 요금은 미터기 기준 부과됨을 확인했습니다.)
  --------------------------
  [예약 상세 정보]
  👤 성함: 
  📞 연락처: 
  ⏰ 날짜/시간: 
  ✈️ 편명(선택): 
  🧳 수하물(개수/종류): 
  💳 결제방식: 
  
  위 내용으로 예약 가능한지 확인 부탁드려요!`;

    navigator.clipboard
      .writeText(summaryText)
      .then(() => {
        alert(
          "📝 예약 양식이 복사되었습니다!\n카카오톡창에 '붙여넣기' 후 남은 정보를 입력해주세요😊"
        );
        window.open("https://pf.kakao.com/_cZxefn/chat", "_blank");
      })
      .catch(() => {
        window.open("https://pf.kakao.com/_cZxefn/chat", "_blank");
      });
  };

  const renderSearchResults = () => (
    <ul className="search-results">
      {searchResults.map((p, i) => (
        <li key={i} onClick={() => selectPlace(p)}>
          <div style={{ display: "flex", alignItems: "flex-start" }}>
            <span style={{ marginRight: "8px", fontSize: "18px" }}>
              {categoryIcon(p.category_group_code)}
            </span>
            <div>
              <strong style={{ fontSize: "15px" }}>{p.place_name}</strong>
              {p.category_name && (
                <div
                  style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}
                >
                  {p.category_name}
                </div>
              )}
              <div
                style={{ fontSize: "12px", color: "#555", marginTop: "2px" }}
              >
                {p.address_name}
              </div>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="App" style={{ position: "relative" }}>
      {/* ✅ [추가됨] 이미지 프리로딩을 위한 보이지 않는 영역 */}
      <div style={{ display: "none" }}>
        <img src="/loading.gif" alt="preload" />
        <img src="/marker_start@2x.png" alt="preload" />
        <img src="/marker_end@2x.png" alt="preload" />
        <img src="/marker_waypoint@2x.png" alt="preload" />
      </div>

      {isLoading && (
        <div className="loading-overlay">
          <div className="analysis-container">
            <div className="radar-wave"></div>
            <div className="radar-wave delay-1"></div>
            <div className="gongchini-main-circle">
              <img src="/loading.gif" alt="계산 중" className="gongchini-gif" />
            </div>
          </div>
          <div className="loading-message">
            <p className="main-text">예상요금 계산하는 중</p>
            <p className="sub-text">
              실제요금은 여객자동차운수사업법에 따라 미터기로 부과됩니다
            </p>
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "8px",
        }}
      >
        <input
          type="time"
          value={departureTime === "now" ? "" : departureTime}
          onChange={(e) => setDepartureTime(e.target.value)}
          style={{
            padding: "4px",
            fontSize: "14px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        />
      </div>

      <div className="banner-container">
        <img
          src="/banner.png"
          alt="공친이 요금 계산기"
          className="main-banner"
        />
        <div className="kakao-badge">
          <img src="/kakao_logo.png" alt="Powered by Kakao" />
        </div>
      </div>

      {/* 출발지 입력 */}
      <div className="input-wrapper">
        <span className="input-icon">
          <img src="/marker_start@2x.png" alt="출발" />
        </span>
        <input
          value={originInput}
          onChange={(e) => handleInputChange(e, "origin")}
          placeholder="출발지 입력"
        />
        {activeType === "origin" &&
          searchResults.length > 0 &&
          renderSearchResults()}
      </div>

      {/* 경유지 입력 */}
      {waypointInputs.map((wp, idx) => (
        <div key={`wp-${idx}`} className="waypoint-box">
          <div className="input-wrapper">
            <span className="input-icon">
              <img src="/marker_waypoint@2x.png" alt="경유" />
            </span>
            <input
              value={wp}
              onChange={(e) => handleInputChange(e, "waypoint", idx)}
              placeholder={`경유지 ${idx + 1} 입력`}
            />
            <span
              className="delete-waypoint"
              onClick={() => removeWaypoint(idx)}
            >
              ✕
            </span>
            {activeType === "waypoint" &&
              activeIndex === idx &&
              searchResults.length > 0 &&
              renderSearchResults()}
          </div>
        </div>
      ))}

      {/* 도착지 입력 */}
      <div className="input-wrapper">
        <span className="input-icon">
          <img src="/marker_end@2x.png" alt="도착" />
        </span>
        <input
          value={destInput}
          onChange={(e) => handleInputChange(e, "destination")}
          placeholder="도착지 입력"
        />
        {activeType === "destination" &&
          searchResults.length > 0 &&
          renderSearchResults()}
      </div>

      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "10px",
          justifyContent: "center",
        }}
      >
        <button className="add-waypoint" onClick={addWaypoint}>
          경유지 추가
        </button>
        <button className="find-route" onClick={findRoute}>
          경로 찾기
        </button>
        <button className="reset" onClick={resetAll}>
          다시입력
        </button>
      </div>

      <div id="map"></div>

      {info && (
        <div
          ref={cardRef}
          className="result-card"
          style={{
            marginTop: "15px",
            padding: "20px",
            border: "1px solid #ddd",
            borderRadius: "10px",
            backgroundColor: "#fff",
            width: "90%",
            maxWidth: "340px",
            marginLeft: "auto",
            marginRight: "auto",
            textAlign: "left",
          }}
        >
          <h3
            style={{
              marginTop: 0,
              marginBottom: "15px",
              backgroundColor: "#f97316",
              color: "#fff",
              padding: "8px 12px",
              borderRadius: "6px",
              fontSize: "16px",
            }}
          >
            🚕 경로 요약
          </h3>
          <p>
            <strong>📍거리:</strong> {info.distance} km
          </p>
          <p>
            <strong>🕒소요시간:</strong> {info.duration} 분
          </p>
          <p>
            <strong>💰예상요금:</strong>
            <br />
            <span
              style={{ fontSize: "30px", fontWeight: "bold", color: "#f97316" }}
            >
              ₩{info.fare}
            </span>
            <span
              style={{ fontSize: "14px", color: "#f97316", marginLeft: "6px" }}
            >
              (+통행료 별도)
            </span>
          </p>
          <button
            onClick={handleReservation}
            style={{
              width: "100%",
              padding: "16px",
              backgroundColor: "#FEE500",
              color: "#191919",
              border: "none",
              borderRadius: "12px",
              fontSize: "16px",
              fontWeight: "bold",
              marginTop: "10px",
            }}
          >
            💬 이 금액으로 지금 바로 예약하기
          </button>
          <p style={{ fontSize: "11px", color: "#999", marginTop: "12px" }}>
            예상 요금은 참고용으로, 교통 상황에 따라 달라질 수 있어요.
            <br />
            실제 요금은 여객자동차운수사업법에 따라 미터기 기준으로 부과됩니다!
          </p>
        </div>
      )}
    </div>
  );
}
